import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Select, Form, Button, Col, Row, Switch } from 'antd'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import styled from '@emotion/styled'
import { useLazyQuery } from '@apollo/client'
import { FETCH_MENUS, GET_LIGHTSPEED_ORDER_PROFILES } from './queries'
import {
  LightspeedMenu,
  LightspeedMenuOrderType,
  LightspeedOrderProfile,
  getMenuOrderType
} from './types'
import { DeleteOutlined } from '@ant-design/icons'
import { CommonButton, StyledFormInput } from 'components/Common'
import { useStoreIntegration } from './hooks/useStoreIntegration'
import { useLocationPageData } from './hooks/useLocationPageData'
import { usePreconnectStore } from './hooks/usePreconnectStore'
import { PartnerName, PARTNER_SLUGS } from '../../constants'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import { useIntegration } from '../../hooks/useIntegration'
import CommonHeader from '../Common/CommonHeader'
import {
  Card,
  FormItem,
  FormLabel,
  FormItemTitle,
  FormItemDescription,
  RemoveButton
} from './styles'
import { toBufferInt } from './utils'

type FormValues = {
  storeId: string
  businessLocationId: string
  sameDayMenuId: string
  preOrderMenuId: string
  delivery: string
  pickup: string
  orderAtTable: string
  sameDayBufferMinutes: string | null
  preOrderBufferMinutes: string | null
}

const LIGHTSPEED_PATH = `/integrations/${PARTNER_SLUGS.LIGHTSPEED}`

const buildMenu = (
  menuId: string,
  orderType: LightspeedMenuOrderType,
  menusData: LightspeedMenu[]
) => {
  if (!menuId) return null
  const name = menusData.find((menu) => menu.id === menuId)?.name
  if (!name) return null
  return { id: menuId, name, orderType }
}

const LocationPage = () => {
  const navigate = useNavigate()
  const { merchantLocations: stores } = useMerchant()
  const { addMessage } = useMessageStore()
  const { disconnectStore, connectStore, syncMenu } = useStoreIntegration()
  const { refetchLightspeedStores } = useIntegration()

  const { selectedBusiness, selectedLocation, connectedLocations } =
    useLocationPageData()

  const { preconnectStore } = usePreconnectStore(
    selectedBusiness,
    connectedLocations
  )

  const [form] = Form.useForm()
  const sameDayBufferValue = Form.useWatch('sameDayBufferMinutes', form)
  const preOrderBufferValue = Form.useWatch('preOrderBufferMinutes', form)
  const [canSubmit, setCanSubmit] = useState(false)
  const [isFormLoading, setIsFormLoading] = useState(false)
  const [pullMenuFromLightspeed, setPullMenuFromLightspeed] = useState(true)
  const [enableFulfilmentTypes, setEnableFulfilmentTypes] = useState(true)

  const [menusData, setMenusData] = useState<LightspeedMenu[]>([])
  const [orderProfilesData, setOrderProfilesData] = useState<
    LightspeedOrderProfile[]
  >([])

  const [
    fetchOrderProfiles,
    { loading: orderProfilesLoading, errors: orderProfilesErrors }
  ] = useLazyQuery<{
    getLightspeedOrderProfiles: LightspeedOrderProfile[]
  }>(GET_LIGHTSPEED_ORDER_PROFILES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setOrderProfilesData(data?.getLightspeedOrderProfiles || [])
    }
  })

  const [fetchMenus, { loading: menusLoading, errors: menusErrors }] =
    useLazyQuery<{ fetchPartnerMenus: LightspeedMenu[] }>(FETCH_MENUS, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      onCompleted: (data) => {
        setMenusData(data?.fetchPartnerMenus || [])
      }
    })

  useEffect(() => {
    if (menusErrors || orderProfilesErrors) {
      handleError()
    }
  }, [menusErrors, orderProfilesErrors])

  useEffect(() => {
    if (selectedLocation) {
      setIsFormLoading(true)

      const promises = [
        fetchOrderProfiles({
          variables: { storeId: selectedLocation.id }
        }),
        fetchMenus({
          variables: {
            partnerName: PartnerName.LIGHTSPEED,
            storeId: selectedLocation.id
          }
        })
      ]

      Promise.all(promises).then(() => {
        setIsFormLoading(false)

        const storedMenus =
          selectedLocation.store_integration_settings.lightspeed_menus || []
        const hasSavedMenus = storedMenus.length > 0
        setPullMenuFromLightspeed(hasSavedMenus)

        const profileMapping =
          selectedLocation.store_integration_settings
            .lightspeed_order_profile_mapping
        setEnableFulfilmentTypes(
          Boolean(
            profileMapping?.delivery ||
              profileMapping?.pickup ||
              profileMapping?.orderAtTable
          )
        )

        const sameDayMenu = storedMenus.find((menu) => {
          const ot = getMenuOrderType(menu)
          return (
            !ot ||
            ot === LightspeedMenuOrderType.ALL ||
            ot === LightspeedMenuOrderType.SAMEDAY
          )
        })
        const preOrderMenu = storedMenus.find(
          (menu) => getMenuOrderType(menu) === LightspeedMenuOrderType.PREORDER
        )

        const businessLocationId =
          selectedLocation.store_integration_settings
            .lightspeed_business_location_id

        form.setFieldsValue({
          storeId: selectedLocation.id,
          businessLocationId,
          sameDayMenuId: sameDayMenu?.id,
          preOrderMenuId: preOrderMenu?.id,
          delivery:
            selectedLocation.store_integration_settings
              .lightspeed_order_profile_mapping?.delivery || '',
          pickup:
            selectedLocation.store_integration_settings
              .lightspeed_order_profile_mapping?.pickup || '',
          orderAtTable:
            selectedLocation.store_integration_settings
              .lightspeed_order_profile_mapping?.orderAtTable || '',
          sameDayBufferMinutes:
            selectedLocation.store_integration_settings
              .lightspeed_scheduled_order_buffer_sameday_minutes != null
              ? String(
                  selectedLocation.store_integration_settings
                    .lightspeed_scheduled_order_buffer_sameday_minutes
                )
              : null,
          preOrderBufferMinutes:
            selectedLocation.store_integration_settings
              .lightspeed_scheduled_order_buffer_preorder_minutes != null
              ? String(
                  selectedLocation.store_integration_settings
                    .lightspeed_scheduled_order_buffer_preorder_minutes
                )
              : null
        })

        setCanSubmit(
          Boolean(
            selectedLocation.id &&
              businessLocationId &&
              (!hasSavedMenus || (sameDayMenu?.id && preOrderMenu?.id))
          )
        )
      })
    }
  }, [selectedLocation])

  const handleError = () => {
    addMessage({
      type: 'message',
      content: 'Your session has expired. Please log in again.'
    })
    navigate('/integrations')
  }

  const computeCanSubmit = (pullMenu: boolean) => {
    const storeId = form.getFieldValue('storeId')
    const businessLocationId = form.getFieldValue('businessLocationId')
    const sameDayMenuId = form.getFieldValue('sameDayMenuId')
    const preOrderMenuId = form.getFieldValue('preOrderMenuId')
    setCanSubmit(
      Boolean(
        storeId &&
          businessLocationId &&
          (!pullMenu || (sameDayMenuId && preOrderMenuId))
      )
    )
  }

  const handleTogglePullMenu = (checked: boolean) => {
    setPullMenuFromLightspeed(checked)
    computeCanSubmit(checked)
  }

  const handleToggleEnableFulfilmentTypes = (checked: boolean) => {
    setEnableFulfilmentTypes(checked)
  }

  const handleChange = async (changedFields: { name: string }[]) => {
    if (!changedFields.length) return

    computeCanSubmit(pullMenuFromLightspeed)

    if (!['storeId', 'businessLocationId'].includes(changedFields[0].name[0])) {
      return
    }

    if (!selectedBusiness) return

    const storeId = form.getFieldValue('storeId')
    const businessLocationId = form.getFieldValue('businessLocationId')

    const businessLocationName = selectedBusiness.businessLocations.find(
      (location) => location.id === businessLocationId
    )?.name

    if (!storeId || !businessLocationId || !businessLocationName) {
      return
    }

    await preconnectStore({
      storeId,
      businessLocationId,
      businessLocationName
    })

    fetchMenus({
      variables: { partnerName: PartnerName.LIGHTSPEED, storeId }
    })

    fetchOrderProfiles({
      variables: { storeId }
    })
  }

  const handleFinish = async ({
    storeId,
    businessLocationId,
    sameDayMenuId,
    preOrderMenuId,
    delivery,
    pickup,
    orderAtTable,
    sameDayBufferMinutes,
    preOrderBufferMinutes
  }: FormValues) => {
    if (!selectedBusiness) return

    const businessLocationName = selectedBusiness.businessLocations.find(
      (location) => location.id === businessLocationId
    )?.name

    const menus = pullMenuFromLightspeed
      ? [
          buildMenu(sameDayMenuId, LightspeedMenuOrderType.SAMEDAY, menusData),
          buildMenu(preOrderMenuId, LightspeedMenuOrderType.PREORDER, menusData)
        ].filter(
          (
            menu
          ): menu is {
            id: string
            name: string
            orderType: LightspeedMenuOrderType
          } => menu !== null
        )
      : []

    if (!businessLocationName) return
    if (pullMenuFromLightspeed && !menus.length) return

    try {
      if (selectedLocation && selectedLocation.id !== storeId) {
        await disconnectStore({ storeId: selectedLocation.id })
      }

      await connectStore({
        storeId,
        businessId: selectedBusiness.id,
        businessName: selectedBusiness.name,
        businessLocationId,
        businessLocationName,
        menus,
        sameDayBufferMinutes: pullMenuFromLightspeed
          ? toBufferInt(sameDayBufferMinutes)
          : null,
        preOrderBufferMinutes: pullMenuFromLightspeed
          ? toBufferInt(preOrderBufferMinutes)
          : null,
        orderProfileMapping: enableFulfilmentTypes
          ? {
              delivery: delivery || null,
              pickup: pickup || null,
              orderAtTable: orderAtTable || null
            }
          : { delivery: null, pickup: null, orderAtTable: null }
      })

      addMessage({
        type: 'message',
        content: pullMenuFromLightspeed
          ? "Your Slerp store and Lightspeed location are now connected. To display your Lightspeed menu on this store, you'll need to sync your menus."
          : 'Your Slerp store and Lightspeed location are now connected.',
        duration: 10
      })

      await refetchLightspeedStores()
      navigate(LIGHTSPEED_PATH)
    } catch (error) {
      addMessage({
        type: 'message',
        content: 'Failed to connect store!'
      })
      handleError()
    }
  }

  const handleRemove = async () => {
    if (!selectedLocation?.id) return

    try {
      await disconnectStore({ storeId: selectedLocation.id })
      addMessage({
        type: 'message',
        content: 'Connection removed successfully!'
      })
      await refetchLightspeedStores()
      navigate(LIGHTSPEED_PATH)
    } catch (error) {
      addMessage({
        type: 'message',
        content: 'Failed to remove connection!'
      })
      handleError()
    }
  }

  const handleBack = async () => {
    await preconnectStore(null)
    navigate(LIGHTSPEED_PATH)
  }

  const handleSyncMenu = async () => {
    if (!selectedLocation?.id) return

    try {
      await syncMenu(selectedLocation.id)
      addMessage({
        type: 'message',
        content: 'Menu synced successfully!'
      })
      await refetchLightspeedStores()
      navigate(LIGHTSPEED_PATH)
    } catch (error) {
      addMessage({
        type: 'message',
        content: 'Failed to sync menu!'
      })
      handleError()
    }
  }

  if (!selectedBusiness) {
    return null
  }

  const slerpStoreOptions = stores.map((store) => ({
    label: store.name,
    value: store.id
  }))

  const lightspeedLocationOptions = selectedBusiness.businessLocations.map(
    (location) => ({
      label: location.name,
      value: location.id
    })
  )

  const lightspeedMenuOptions = menusData.map((menu) => ({
    label: menu.name,
    value: menu.id
  }))

  const lightspeedOrderProfileMapped = orderProfilesData.map((profile) => ({
    label: `${profile.code[0].toUpperCase()}${profile.code.slice(1)}`,
    value: profile.code
  }))

  const lightspeedOrderProfileOptions = [
    { label: 'Disabled', value: '' },
    ...lightspeedOrderProfileMapped
  ]

  return (
    <Root>
      <CommonHeader
        title='Connect Lightspeed'
        onBack={handleBack}
        helpLink='https://support.slerp.com/knowledge/how-do-i-connect-to-lightspeed-k-series'
      />

      <ContentCard>
        <PageTitle>
          {selectedLocation ? 'Update Store Connection' : 'Connect Store'}
        </PageTitle>
        <PageDescription>
          Map your Slerp store to a Lightspeed store. After mapping is complete,
          choose the Lightspeed menu you want to pull into your Slerp store
          inventory.
        </PageDescription>

        <Form
          onFinish={handleFinish}
          onFieldsChange={handleChange}
          form={form}
          initialValues={{
            delivery: '',
            pickup: '',
            orderAtTable: ''
          }}
        >
          <Card isLoading={isFormLoading}>
            <div>
              <FormLabel>
                <FormItemTitle>Slerp Store</FormItemTitle>
                <FormItemDescription>
                  Choose the store to connect to Lightspeed.
                </FormItemDescription>
              </FormLabel>
              <FormItem name='storeId' required>
                <Select
                  placeholder='Select Slerp Store'
                  options={slerpStoreOptions}
                />
              </FormItem>
            </div>

            <div>
              <FormLabel>
                <FormItemTitle>Lightspeed Location</FormItemTitle>
                <FormItemDescription>
                  Changing the Lightspeed store will require you to reselect a
                  menu.
                </FormItemDescription>
              </FormLabel>
              <FormItem name='businessLocationId' required>
                <Select
                  placeholder='Select Lightspeed Location'
                  options={lightspeedLocationOptions}
                />
              </FormItem>
            </div>

            {!!selectedLocation && (
              <RemoveButton type='text' onClick={handleRemove}>
                <span>Delete Connection</span>
                <DeleteOutlined />
              </RemoveButton>
            )}
          </Card>

          <Card gap={0} isLoading={menusLoading}>
            <ToggleRow>
              <Switch
                checked={pullMenuFromLightspeed}
                onChange={handleTogglePullMenu}
              />
              <FormLabel>
                <FormItemTitle>Pull Menu from Lightspeed</FormItemTitle>
                <FormItemDescription>
                  This menu will be used as your Slerp inventory.
                </FormItemDescription>
              </FormLabel>
            </ToggleRow>

            {pullMenuFromLightspeed && (
              <>
                <MenuDivider />
                <StyledRow align='middle'>
                  <MenuLabelCol span={12}>
                    <FormLabel>
                      <FormItemTitle>Select Same Day Menu</FormItemTitle>
                      <FormItemDescription>
                        This menu will be used as your Slerp same day inventory.
                      </FormItemDescription>
                    </FormLabel>
                  </MenuLabelCol>
                  <Col span={12}>
                    <FormItem name='sameDayMenuId' required>
                      <Select
                        placeholder='Select Lightspeed Menu'
                        options={lightspeedMenuOptions}
                      />
                    </FormItem>
                  </Col>
                </StyledRow>

                <BufferRow>
                  <FormLabel style={{ flex: 1, marginBottom: 0 }}>
                    <FormItemTitle>Buffer Orders</FormItemTitle>
                    <FormItemDescription>
                      Set how many minutes before pickup the kitchen receives
                      the order. Leave blank to send instantly and have the POS
                      accept it. ASAP orders are always sent instantly. If you
                      set a buffer, enable auto-accept so the order isn't left
                      pending until it reaches the POS.
                    </FormItemDescription>
                  </FormLabel>
                  <FormItem name='sameDayBufferMinutes'>
                    <BufferInput
                      type='number'
                      min={0}
                      suffix='Min'
                      textAlign='right'
                      hasValue={Boolean(sameDayBufferValue)}
                    />
                  </FormItem>
                </BufferRow>

                <MenuDivider />

                <StyledRow align='middle'>
                  <MenuLabelCol span={12}>
                    <FormLabel>
                      <FormItemTitle>Select Pre-Order Menu</FormItemTitle>
                      <FormItemDescription>
                        This menu will be used as your Slerp <br />
                        pre-order inventory.
                      </FormItemDescription>
                    </FormLabel>
                  </MenuLabelCol>
                  <Col span={12}>
                    <FormItem name='preOrderMenuId' required>
                      <Select
                        placeholder='Select Lightspeed Menu'
                        options={lightspeedMenuOptions}
                      />
                    </FormItem>
                  </Col>
                </StyledRow>

                <BufferRow>
                  <FormLabel style={{ flex: 1, marginBottom: 0 }}>
                    <FormItemTitle>Buffer Orders</FormItemTitle>
                    <FormItemDescription>
                      Set how many minutes before pickup the kitchen receives
                      the order. Leave blank to send instantly and have the POS
                      accept it. If you set a buffer, enable auto-accept so the
                      order isn't left pending until it reaches the POS.
                    </FormItemDescription>
                  </FormLabel>
                  <FormItem name='preOrderBufferMinutes'>
                    <BufferInput
                      type='number'
                      min={0}
                      suffix='Min'
                      textAlign='right'
                      hasValue={Boolean(preOrderBufferValue)}
                    />
                  </FormItem>
                </BufferRow>
              </>
            )}
          </Card>

          <Card gap={0} isLoading={orderProfilesLoading}>
            <ToggleRow>
              <Switch
                checked={enableFulfilmentTypes}
                onChange={handleToggleEnableFulfilmentTypes}
              />
              <FormLabel>
                <FormItemTitle>Send orders to Lightspeed</FormItemTitle>
                <FormItemDescription>
                  To sync a fulfillment type with Lightspeed, map it to an Order
                  Profile. Unmapped types won't be sent to the POS.
                </FormItemDescription>
              </FormLabel>
            </ToggleRow>

            {enableFulfilmentTypes && (
              <>
                <MenuDivider />
                <div>
                  <StyledRow>
                    <Col span={12}>
                      <FormItemTitle>Slerp Fulfiment type</FormItemTitle>
                    </Col>
                    <Col span={12}>
                      <FormItemTitle>Lightspeed order profile</FormItemTitle>
                    </Col>
                  </StyledRow>
                  <StyledRow align='middle'>
                    <Col span={12}>
                      <FormItemTitle>Delivery</FormItemTitle>
                    </Col>
                    <Col span={12}>
                      <FormItem name='delivery'>
                        <Select
                          options={lightspeedOrderProfileOptions}
                          placeholder='Select Delivery Profile'
                        />
                      </FormItem>
                    </Col>
                  </StyledRow>
                  <StyledRow align='middle'>
                    <Col span={12}>
                      <FormItemTitle>Pickup</FormItemTitle>
                    </Col>
                    <Col span={12}>
                      <FormItem name='pickup'>
                        <Select
                          options={lightspeedOrderProfileOptions}
                          placeholder='Select Pickup Profile'
                        />
                      </FormItem>
                    </Col>
                  </StyledRow>
                  <StyledRow align='middle'>
                    <Col span={12}>
                      <FormItemTitle>Order at table</FormItemTitle>
                    </Col>
                    <Col span={12}>
                      <FormItem name='orderAtTable'>
                        <Select
                          options={lightspeedOrderProfileOptions}
                          placeholder='Select Table Order Profile'
                        />
                      </FormItem>
                    </Col>
                  </StyledRow>
                </div>
              </>
            )}
          </Card>
        </Form>
      </ContentCard>

      <ContentCard padding={12}>
        <Actions>
          {!!selectedLocation && (
            <CommonButton onClick={handleSyncMenu} disabled={!canSubmit}>
              <span>Sync menu</span>
            </CommonButton>
          )}

          <Divider />
          <CommonButton onClick={handleBack}>Cancel</CommonButton>
          <SubmitButton onClick={() => form.submit()} disabled={!canSubmit}>
            {selectedLocation ? 'Update' : 'Save'}
          </SubmitButton>
        </Actions>
      </ContentCard>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
`

const Divider = styled.div`
  flex-grow: 1;
`

const ContentCard = styled.div<{ padding?: number }>(
  ({ padding = 24 }) => `
  margin: 0 auto;
  width: 100%;
  max-width: 640px;
  padding: ${padding}px 24px;
  background-color: #fff;
  border-radius: 6px;
`
)

const PageTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #4a4a68;
`

const PageDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #4a4a68;
  margin-bottom: 24px;
`

const StyledRow = styled(Row)`
  margin-bottom: 6px;
`

const MenuLabelCol = styled(Col)`
  padding-right: 12px;
`

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  > div {
    margin-bottom: 0;
  }
`

const MenuDivider = styled.hr`
  border: none;
  border-top: 1px solid #e7e5e9;
  margin: 16px 0;
`

const BufferRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 6px;
`

const BufferInput = styled(StyledFormInput, {
  shouldForwardProp: (prop) => prop !== 'hasValue'
})<{ hasValue?: boolean }>`
  .ant-input-suffix {
    color: ${({ hasValue }) => (hasValue ? '#4b4a67' : '#a4a4b3')} !important;
  }
`

const Actions = styled.div`
  display: flex;
  gap: 16px;

  > button {
    width: fit-content;
    min-width: 80px;
    max-width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const SubmitButton = styled(Button)`
  width: 100%;
`

export default LocationPage
